import {useEffect, useState} from 'react';
import {useSession} from "../../firebase/UserProvider";
import {nanoid} from "nanoid";
import {apiCreateTag, apiDeleteTag} from "../../utils/api/tags";

export default function useDocumentTags({incomingTags = [], docId}) {
    const {user} = useSession()
    const [tags, setTags] = useState(incomingTags);
    const [isPosting, setIsPosting] = useState(false);
    const [loadingTagOps, setLoadingTagOps] = useState({});

    useEffect(() => {
        setTags(incomingTags);
    }, [incomingTags]);

    // A tag is considered voted if the tag object has an operationId.
    // Support being passed either a tag object or a tag name (string).
    function isVoted(tagOrName) {
        if (!tagOrName) return false;
        if (typeof tagOrName === 'string') {
            const idx = findTagIndex(tags, tagOrName);
            return idx !== -1 && !!(tags[idx]?.operationId);
        }
        // assume it's a tag-like object
        return !!tagOrName.operationId;
    }

    // helper: robustly extract vote count from various server responses
    function getVoteCountFromResp(resp) {
        if (!resp) return 0;
        if (typeof resp.voteCount === 'number') return Number(resp.voteCount);
        return 0;
    }

    // helper: find tag index by case-insensitive match against .tag or .name
    function findTagIndex(list, tag) {
        const t = (tag || '').trim().toLowerCase();
        return list.findIndex(x => ((x.tag || x.name) || '').toString().trim().toLowerCase() === t);
    }

    // helper: ensure tag object shape when creating/updating state
    function normalizeTagObj(obj) {
        return {
            name: obj.name,
            voteCount: typeof obj.voteCount === 'number' ? Number(obj.voteCount) : 0,
            operationId: obj.operationId ?? null,
        };
    }

    async function createTag(tag, newOperationId) {
        const tRaw = (tag || '').trim().toLowerCase();
        if (!tRaw) return;

        setIsPosting(true);
        const tLower = tRaw;

        try {
            const reqObj = {
                operationId: newOperationId ? newOperationId : nanoid(),
                name: tRaw,
                entityType: "DOCUMENT",
                entity: {
                    docId: docId,
                },
                userId: user?.uid
            }

            // Optimistic update: add tag locally with 1 vote and our operationId
            setTags(prev => {
                if (prev.some(x => (x.name).toString().trim().toLowerCase() === tLower)) return prev;
                return [{...normalizeTagObj({...reqObj, voteCount: 1})}, ...prev];
            });

            await apiCreateTag(reqObj);

            // No need to update again, optimistic already set operationId

        } catch (e) {
            console.error('Failed to create tag', e);
            // rollback optimistic add
            setTags(prev => prev.filter(x => ((x.name).toString().trim().toLowerCase() !== tLower)));
            throw e;
        } finally {
            setIsPosting(false);
        }
    }

    async function voteTag(tag) {
        const tRaw = (tag || '').trim();
        if (!tRaw) return;
        const tLower = tRaw.toLowerCase();

        // pre-read existing operationId for this tag
        const currentTag = tags.find(x => ((x.name).toString().trim().toLowerCase() === tLower));
        const existingOperationId = currentTag?.operationId ?? null;

        if (loadingTagOps[tLower]) return;
        setLoadingTagOps(prev => ({...prev, [tLower]: true}));

        // capture previous state for rollback
        let prevTags = null;
        try {
            // Capture a deep copy of the current tags for safe rollback
            prevTags = Array.isArray(tags) ? tags.map(x => ({...x})) : [];

            // determine whether user had already voted: user vote is represented by tag.operationId
            const alreadyNow = !!existingOperationId;

            // optimistic update: increment or add
            setTags(prev => {
                const idx = findTagIndex(prev, tRaw);
                const voted = alreadyNow; // use the pre-read value
                const next = prev.map(x => ({...x}));

                if (idx === -1) {
                    // not present
                    if (!voted) {
                        // No tag exists and user hasn't voted -> delegate to createTag which handles optimistic add
                        return next;
                    }
                    return next;
                }

                const existing = {...next[idx]};
                const currentVotes = typeof existing.voteCount === 'number' ? existing.voteCount : 0;

                if (voted) {
                    // remove vote: set operationId to null, decrement voteCount, if 0 remove
                    existing.operationId = null;
                    existing.voteCount = Math.max(0, currentVotes - 1);
                    if (existing.voteCount === 0) {
                        next.splice(idx, 1);
                        return next;
                    }
                    next[idx] = existing;
                    return next;
                } else {
                    // add vote => increment
                    existing.voteCount = currentVotes + 1;
                    next[idx] = existing;
                    return next;
                }
            });

            // decide path based on whether user had already voted before this action
            if (!alreadyNow) {
                const newOperationId = nanoid();

                // set tag.operationId optimistically
                setTags(prev => {
                    const idx = findTagIndex(prev, tRaw);
                    if (idx === -1) return prev;
                    const updated = {...prev[idx], operationId: newOperationId};
                    const next = prev.map(x => ({...x}));
                    next[idx] = updated;
                    return next;
                });

                // user is voting: delegate to createTag to create an operation and update operationId
                return await createTag(tRaw, newOperationId);
            } else {
                // user is removing their vote -> must delete using operationId
                // use the operationId we read earlier; if missing, we cannot delete on server
                if (!existingOperationId) {
                    // rollback
                    setTags(prevTags);
                    return Promise.reject(new Error('No operationId found for tag; cannot remove vote on server'));
                }

                return await removeTag(existingOperationId, tRaw);
            }
        } catch (e) {
            console.error('Vote failed', e);
            // rollback to previous tags state if we captured it
            if (Array.isArray(prevTags)) {
                setTags(prevTags);
            }
            throw e;
        } finally {
            setLoadingTagOps(prev => ({...prev, [tLower]: false}));
        }
    }

    // removeTag now expects an operationId (server operation id) and optional tag name for state updates
    async function removeTag(operationId, tagName) {
        if (!operationId) throw new Error('operationId is required to remove a tag');
        const tRaw = (tagName || '').trim();
        const tLower = tRaw.toLowerCase();
        if (tRaw && loadingTagOps[tLower]) return;
        if (tRaw) setLoadingTagOps(prev => ({...prev, [tLower]: true}));

        try {
            const resp = await apiDeleteTag(operationId);
            const serverVotes = getVoteCountFromResp(resp);

            if (tRaw) {
                // apply rule: if serverReports votes > 0, re-add with operationId null
                if (serverVotes > 0) {
                    setTags(prev => {
                        if (findTagIndex(prev, tRaw) !== -1) {
                            // update existing entry
                            return prev.map(x => ((x.name || x.tag) || '').toString().trim().toLowerCase() === tLower ? {
                                ...normalizeTagObj({...x, voteCount: serverVotes, operationId: null})
                            } : x);
                        }
                        return [{
                            ...normalizeTagObj({
                                name: tRaw,
                                voteCount: serverVotes,
                                operationId: null
                            })
                        }, ...prev];
                    });
                }
            }

            return resp;
        } catch (e) {
            console.error('Failed to remove tag', e);
            throw e;
        } finally {
            if (tRaw) setLoadingTagOps(prev => ({...prev, [tLower]: false}));
        }
    }

    return {
        tags,
        createTag,
        voteTag,
        removeTag,
        isVoted,
        isPosting,
        loadingTagOps
    };
}
